import { LayoutDashboard, Pin } from "lucide-react";
import { useDashboards } from "@/hooks/dashboard/useDashboards";
import { usePinnedDashboards } from "@/hooks/dashboard/usePinnedDashboards";
import type { DashboardRecord } from "@/lib/dashboard/types";
import type { SidebarItem } from "@/types/navigation";

/**
 * Workspace is one list: dashboards published for the person and the ones
 * they created, told apart by a Source column and facet.
 */
export const DASHBOARDS_NAV: SidebarItem[] = [
  {
    id: "workspace-dashboards",
    label: "Dashboards",
    href: "/workspace/dashboards",
    icon: LayoutDashboard,
  },
];

/** The person's pinned dashboards, in pin order, among those they can still open. */
export function pinnedDashboards(
  dashboards: readonly DashboardRecord[],
  pinnedIds: readonly string[],
): DashboardRecord[] {
  const byId = new Map(
    dashboards
      .filter((dashboard) => dashboard.kind !== "template" && dashboard.state !== "draft")
      .map((dashboard) => [dashboard.id, dashboard]),
  );
  return pinnedIds.flatMap((id) => {
    const dashboard = byId.get(id);
    return dashboard ? [dashboard] : [];
  });
}

/** The workspace navigation, with a "Pinned" group once the person has pinned dashboards. */
export function getDashboardsNav(
  dashboards: readonly DashboardRecord[],
  pinnedIds: readonly string[] = [],
): SidebarItem[] {
  const pinned = pinnedDashboards(dashboards, pinnedIds);
  if (pinned.length === 0) return DASHBOARDS_NAV;
  return [
    ...DASHBOARDS_NAV,
    {
      id: "workspace-pinned",
      label: "Pinned",
      href: "",
      icon: Pin,
      defaultOpen: true,
      children: pinned.map((dashboard) => ({
        id: `workspace-pinned-${dashboard.id}`,
        label: dashboard.name,
        href: `/workspace/dashboards/${encodeURIComponent(dashboard.slug)}`,
      })),
    },
  ];
}

export function useDashboardsNav() {
  const { ids } = usePinnedDashboards();
  // Same query as Home: already cached in the places this navigation appears.
  const { data } = useDashboards({ relationship: "accessible", state: "saved" });
  return getDashboardsNav(ids.length ? (data ?? []) : [], ids);
}
